import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Trophy, Medal } from 'lucide-react';
import api from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';
import { presenceStatus, PRESENCE_COLORS } from '../../utils/presence';

export const meta = {
  id: 'leaderboard',
  category: 'team',
  titleKey: 'dashboard.widgets.leaderboard',
  descriptionKey: 'dashboard.widgets.leaderboardDesc',
  defaultSize: 'sm',
  icon: Trophy,
};

export default function LeaderboardWidget() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [lbPeriod, setLbPeriod] = useState('week');

  const { data: leaderboardData } = useQuery({
    queryKey: ['dashboard-leaderboard', lbPeriod],
    queryFn: () => api.get('/dashboard/leaderboard', { params: { period: lbPeriod } }).then(r => r.data),
    refetchInterval: 60000,
  });

  return (
    <div className="card" style={{ height: '100%' }}>
      <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Trophy size={16} />
          {t('dashboard.leaderboard')}
        </div>
        <div className="period-toggle">
          {[
            { key: 'today', label: t('dashboard.today') },
            { key: 'week', label: t('dashboard.thisWeek') },
            { key: 'month', label: t('dashboard.thisMonth') },
          ].map(p => (
            <button key={p.key} className={lbPeriod === p.key ? 'active' : ''} onClick={() => setLbPeriod(p.key)}>
              {p.label}
            </button>
          ))}
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '0 12px 6px', fontSize: 10, fontWeight: 600, color: 'var(--c-text-subtle)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
        <span style={{ width: 20 }}>#</span>
        <span style={{ flex: 1 }}></span>
        <span style={{ width: 44, textAlign: 'center' }}>{t('dashboard.resolved')}</span>
        <span style={{ width: 48, textAlign: 'center' }}>{t('dashboard.avgResponse')}</span>
        <span style={{ width: 40, textAlign: 'center' }}>{t('dashboard.hoursLogged')}</span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {(leaderboardData || []).map((agent, idx) => {
          const rank = idx + 1;
          const isSelf = agent.id === user?.id;
          const presence = presenceStatus(agent);
          const medalColors = { 1: '#FFD700', 2: '#C0C0C0', 3: '#CD7F32' };
          return (
            <div
              key={agent.id}
              className={`leaderboard-row ${isSelf ? 'leaderboard-row-self' : ''}`}
              style={{ cursor: 'pointer' }}
              onClick={() => navigate(`/tickets?assigned_to=${agent.id}&status=__active__`)}
            >
              <span style={{ width: 20, textAlign: 'center', fontSize: 13, fontWeight: 700 }}>
                {rank <= 3
                  ? <Medal size={16} style={{ color: medalColors[rank] }} />
                  : <span style={{ color: 'var(--c-text-muted)', fontSize: 12 }}>{rank}</span>}
              </span>
              <div style={{ position: 'relative', flexShrink: 0 }}>
                <div className="user-avatar" style={{ width: 28, height: 28, fontSize: 11 }}>
                  {agent.avatar_url
                    ? <img src={agent.avatar_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
                    : `${agent.first_name?.[0] || ''}${agent.last_name?.[0] || ''}`}
                </div>
                <div style={{
                  position: 'absolute', bottom: -1, right: -1,
                  width: 9, height: 9, borderRadius: '50%',
                  background: PRESENCE_COLORS[presence],
                  border: '2px solid var(--c-surface)',
                }} />
              </div>
              <div style={{ flex: 1, minWidth: 0, fontSize: 12, fontWeight: isSelf ? 600 : 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {agent.first_name} {agent.last_name}
              </div>
              <span style={{ width: 44, textAlign: 'center', fontSize: 13, fontWeight: 700, color: parseInt(agent.resolved) > 0 ? 'var(--c-resolved)' : 'var(--c-text-muted)' }}>
                {agent.resolved || 0}
              </span>
              <span style={{ width: 48, textAlign: 'center', fontSize: 11, color: 'var(--c-text-muted)' }}>
                {agent.avg_response_hrs ? `${parseFloat(agent.avg_response_hrs).toFixed(1)}h` : '—'}
              </span>
              <span style={{ width: 40, textAlign: 'center', fontSize: 11, color: 'var(--c-text-muted)' }}>
                {parseFloat(agent.hours_logged) > 0 ? `${agent.hours_logged}h` : '—'}
              </span>
            </div>
          );
        })}
        {(!leaderboardData || leaderboardData.length === 0) && (
          <div style={{ padding: 16, textAlign: 'center', color: 'var(--c-text-muted)', fontSize: 12 }}>—</div>
        )}
      </div>
    </div>
  );
}
